import * as PropTypes from 'prop-types';

import * as ArchiveConstants from 'archive/ArchiveConstants';
import {Permissions} from 'auth/PermissionConstants';
import {FolderSummary, NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import {BioentityAliasInfo, TagSchema} from 'constants/RegistryConstants';
import {User} from 'constants/UserConstants';
import {BioentityNote, BioentityServerNote, Note} from 'notebook/util/NoteUtilConstants';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';
import {BenchlingBlob} from 'schemas/tags/BlobConstants';
import {COMPUTED_STATE_SHAPE, ComputedState} from 'schemas/tags/computed/ComputedTagConstants';
import {MakeFieldsRequired} from 'util/Types';

export interface TagsDict {
  [fieldName: string]: Tag;
}

export interface SchemaTagsDict {
  [fieldName: string]: SchemaTag;
}

export interface TargetFile {
  id: string;
  name: string;
  url: string;
}

export interface TargetStorable {
  id: string;
  iconId: string;
  name: string;
}

export interface BatchInfo {
  id: string | null;
  name: string;
  fileId: string | null;
  iconId: string | null;
  url?: string;
}

export interface TargetAssayRecord {
  id: string | null;
}

export type TargetRequest = TargetAssayRecord;

interface TargetExtantEntry {
  id: string;
  name: string;
  url: string;
  humanId: string | null;
}

interface TargetDeletedEntry {
  id: null;
  name: string;
}

export type TargetEntry = TargetExtantEntry | TargetDeletedEntry;

export type TargetLinkable =
  | TargetAssayRecord
  | BenchlingBlob
  | TargetFile
  | BatchInfo
  | TargetEntry
  | TargetRequest
  | TargetStorable
  | TargetSample;

export const BATCH_INFO_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  fileId: PropTypes.string.isRequired,
  iconId: PropTypes.string.isRequired,
});

export interface TargetSample {
  id: string;
  name: string;
  url: string;
}

// TODO(TypeScript): Bring this inline with the server dict format (right now, a bunch of the keys
// are optional to make the type def compatible with tag objects generated in TagEditorModal)
export interface CustomTag {
  name: string;
  id?: number;
  value?: string;
  url?: string;
  display?: string;
  sortable?: boolean;
}

// KEEP UP TO DATE WITH TAG_FIELDS BELOW
// This shape is a union of the different to_dict() values from all different SchemaTag types in the backend
export interface SchemaTag {
  computedState?: ComputedState | null | undefined;
  name: string;
  floatValue?: number | null;
  integerValue?: number | null;
  booleanValue?: boolean | null;
  datetimeValue?: string | null;
  dateValue?: string | null;
  schemaFieldSelectorOptionIds?: Array<string> | null;
  targetResults?: Array<TargetAssayRecord> | null;
  targetAssayRuns?: Array<TargetAssayRecord> | null;
  targetAssayRequests?: Array<TargetRequest> | null;
  sourceEntry?: {editURL: string} | null;
  recordedAt?: string | null;
  tagSchemaField: SchemaFieldConstants.SchemaField;
  targetBlobs?: Array<BenchlingBlob> | null;
  targetFiles?: Array<TargetFile> | null;
  targetFileTagSchemaId?: string | null;
  targetBatches?: Array<BatchInfo> | null;
  targetEntries?: Array<TargetEntry> | null;
  targetStorables?: Array<TargetStorable> | null;
  targetSamples?: Array<TargetSample> | null;
  jsonValue?: object | null;
  url?: string | null;
  value?: string | null;
  rawValue?: string | null;
}

/**
 * A "SchemaFieldDataType" is a less restrictive version of a SchemaField. It does not require
 * a backing model in the database; it merely *describes* the data type of a schema field.
 */
export type SchemaFieldDataType = Pick<
  SchemaFieldConstants.SchemaField,
  'fieldType' | 'requiredLink' | 'isMulti' | 'isRequired'
>;

/**
 * An "unaffiliated" schema tag is a less restrictive version of a SchemaTag, which does not
 * require a backing schemaField model; instead, we *describe* the schema field data type.
 */
export type UnaffiliatedSchemaTag = Omit<SchemaTag, 'tagSchemaField' | 'name'> & {
  name?: string;
  tagSchemaField: SchemaFieldDataType;
};

/**
 * A minimal version of SchemaFieldDataType.
 */
export type MinimalSchemaField = Pick<SchemaFieldConstants.SchemaField, 'fieldType' | 'name'>;

/**
 * A minimal representation of a SchemaTag, which only describes the schema field data.
 */
export type MinimalSchemaTag = Omit<SchemaTag, 'tagSchemaField' | 'name'> & {
  name?: string;
  tagSchemaField: MinimalSchemaField;
};

/**
 * The two type-pairs are linked:
 *   SchemaFieldDataType is contained within an UnaffiliatedSchemaTag;
 *   SchemaField is contained within a (full) SchemaTag
 */
export type ContainingSchemaTagType<T extends SchemaFieldConstants.SchemaField | SchemaFieldDataType> =
  T extends SchemaFieldConstants.SchemaField ? SchemaTag : UnaffiliatedSchemaTag;

/**
 * Inspects an object which may be a SchemaField or an unbacked SchemaFieldDataType
 * to determine if it has a backing model
 */
export function isSchemaFieldDatabaseBacked(
  schemaField: SchemaFieldConstants.SchemaField | SchemaFieldDataType | MinimalSchemaField
): schemaField is SchemaFieldConstants.SchemaField {
  return (schemaField as SchemaFieldConstants.SchemaField).apiId != null;
}

export function isAffiliatedSchemaTag(
  tag: SchemaTag | UnaffiliatedSchemaTag | MinimalSchemaTag
): tag is SchemaTag {
  return isSchemaFieldDatabaseBacked(tag.tagSchemaField);
}

export type ComputedSchemaTag = MakeFieldsRequired<SchemaTag, 'computedState'>;

export type Tag = CustomTag | SchemaTag;

/**
 * Guarantees that a [CustomTag | SchemaTag | UnaffiliatedSchemaTag | MinimalSchemaTag] is a SchemaTag
 */
export function isSchemaTag(tag: Tag | UnaffiliatedSchemaTag | MinimalSchemaTag): tag is SchemaTag {
  return Boolean((tag as SchemaTag).tagSchemaField) && isAffiliatedSchemaTag(tag as SchemaTag);
}

export function isCustomTag(tag: Tag | UnaffiliatedSchemaTag | MinimalSchemaTag): tag is CustomTag {
  return (tag as SchemaTag).tagSchemaField == null;
}

export type FileLinkSchemaTag = Omit<SchemaTag, 'targetFiles'> & {
  targetFiles: Array<TargetFile>;
};

export const TAG_FIELDS = {
  name: PropTypes.string.isRequired,
  // Float tags
  floatValue: PropTypes.number,
  // Integer tags
  integerValue: PropTypes.number,
  // Dropdown tags
  schemaFieldSelectorOptionIds: PropTypes.arrayOf(PropTypes.string),
  sourceEntry: PropTypes.shape({
    editURL: PropTypes.string.isRequired,
  }),
  recordedAt: PropTypes.string,
  // tagSchemaField is required for all schema tags
  tagSchemaField: SchemaFieldConstants.SCHEMA_FIELD_SHAPE,
  // Bioentity link tags
  targetFiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ),
  // Batch link tags
  targetBatches: PropTypes.arrayOf(
    PropTypes.shape({
      fileId: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  // Computed tags
  computedState: COMPUTED_STATE_SHAPE,
  // Custom tags and text schema tags
  url: PropTypes.string,
  value: PropTypes.node,
  rawValue: PropTypes.string,
};

export const TAG_SHAPE = PropTypes.shape(TAG_FIELDS);

export interface BioentityInfo {
  id: string;
  name: string;
  editURL?: string;
  displayLabel?: string;
}

export const BIOENTITY_INFO_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export interface FileSummary {
  archiveRecord: ArchiveConstants.ArchiveRecord | null;
  created_at: string;
  editURL: string;
  folder: FolderSummary;
  id: string;
  name: string;
  tagSchema: TagSchema | null;
  tags: Array<Tag>;
  projectName?: string; // not all endpoints return this property
}

export interface BioentitySummary extends FileSummary {
  bioentityAliasInfos: Array<BioentityAliasInfo>;
  displayLabel: string;
  isBiologicsOwned?: boolean | null;
  modified_at: string;
  projectFolder?: NumberIDFolder | StringIDFolder;
  projectFolderId?: string;
  schemaValidationState: SchemaValidationState | null;
}

export interface Bioentity extends BioentitySummary {
  authors: Array<User>;
  permissions: Permissions;
}

export interface BasicFolderItem extends Bioentity {
  note: BioentityNote | BioentityServerNote;
  thirdPartyStorageServiceData: {
    lastSentTimestamp: string | null;
    lastSentUser: User | null;
  };
}

export const TAG_SCHEMA = PropTypes.shape({
  name: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(SchemaFieldConstants.SCHEMA_FIELD_SHAPE).isRequired,
});

export const TAG_SCHEMA_SUMMARY = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export interface ThirdPartyStorageServiceData {
  lastSentTimestamp: string | null;
  lastSentUser: User | null;
  isSendingToPatentsafe?: boolean;
}

export const FilePreferences = {
  HIGHLIGHT_COMPATIBLE: 'highlight-compatible',
  WORK_SPACE_BUFFERS_NAMESPACE: 'workspaceBuffers.',
  SPLIT_WORKSPACE_NAMESPACE: 'splitWorkspace.',
};

export interface BaseFolderItemFileData {
  archiveRecord: ArchiveConstants.ArchiveRecord | null;
  authors: Array<User>;
  editURL: string;
  folder: StringIDFolder;
  id: string;
  name: string;
  permissions: Permissions;
  tags: Array<Tag>;
  tagSchema: TagSchema | null;
  thirdPartyStorageServiceData: ThirdPartyStorageServiceData;

  created_at: string; // FolderItemSummary fragment's REST representation uses snake case
  modified_at: string; // FolderItemSummary fragment's REST representation uses snake case

  // Only present for bioentities:
  bioentityAliasInfos?: Array<BioentityAliasInfo>;
  displayLabel?: string;
  note?: Note;
  projectFolderId?: string | null;
  schemaValidationState?: SchemaValidationState | null;
}

export type BaseFolderItemFileDataWithNumberIDFolder = Omit<BaseFolderItemFileData, 'folder'> & {
  folder: NumberIDFolder;
};
